import { useEffect, useRef, useState } from 'react'
import { useContextSelectedMotifs } from '../../../contexts/selectedMotifContext'
import { EXPORT_SIZE, useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { FrameOfMotifs } from './frameOfMotifs'
import { isAndroid, isIOS } from 'react-device-detect'
import { useThrottleFn } from '@react-cmpt/use-throttle'
import { PositionXY } from '../../../types/PositionXY'
import { getPagePositionXY } from '../../../utils/getPagePositionXY'
import { JSONExportPlane, MotifGeneratorType } from '../types'
import { getTransformProperties } from '../util'
import { ExportLayoutModalResponse } from '../ExportLayoutModal'
import { isSafari, SAFARI_MAX_CANVAS_SIZE } from '../../../utils/safariCanvasUtils'

const TILE_NUMBER = 9
const RESOLUTION_MULTIPLIER = 2
const EXPORT_RESOLUTION_MULTIPLIER = 2
const TILES_MULTIPLIER = 3

type Props = {
  rotationAngleProp?: number
  exportResponse?: ExportLayoutModalResponse
}

const SquareLayoutGeneratorCanvas = ({ rotationAngleProp, exportResponse }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [firstTimeGenerated, setFirstTimeGenerated] = useState<Boolean>(false)
  const canvasToExportRef = useRef<HTMLCanvasElement>(null)
  const lastPos = useRef<PositionXY>({ x: 0, y: 0 })
  const dragging = useRef<boolean>(false)
  const { selectedMotifs } = useContextSelectedMotifs()
  const canvasRefs = useRef<HTMLCanvasElement[]>([])
  const [images, setImages] = useState<HTMLImageElement[]>([])
  const [canvasDataUrl, setCanvasDataUrl] = useState<string | null>(null)
  const {
    exportInvoked,
    exportModal,
    startExport,
    setStartExport,
    exportGeneratorCoordinatesToJSON,
    handleExport,
    handleExportCanvas,
  } = useContextMotifGenerator()
  const keepExportCanvas = useRef<HTMLCanvasElement | null>(null)
  const defaultRotationAngle = 0
  const [imageLoadingIsFinished, setImageLoadingIsFinished] = useState<boolean>(false)
  const [rotationAngle, setRotationAngle] = useState(rotationAngleProp || defaultRotationAngle)
  const jsonCoordinates = useRef<JSONExportPlane>({
    clipPlane: undefined,
    entries: [],
  })

  const loadImages = (index: number) => {
    const img = new Image()
    img.crossOrigin = ''
    const motif = selectedMotifs[index]
    img.src = motif.originalSVGUrl || motif.vectorizedUrl!
    img.onload = () => {
      setImages([...images, img])
    }
  }

  const sortImages = (imagesToSort: HTMLImageElement[], sortByArray: string[]) => {
    return [...imagesToSort].sort((a, b) => sortByArray.indexOf(a.src) - sortByArray.indexOf(b.src))
  }

  // Prevents pull-to refresh behavior on iOS devices
  useEffect(() => {
    if (!containerRef.current) return
    let prevent = false

    containerRef.current.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return

      const scrollY =
        window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop
      prevent = scrollY === 0
    })

    containerRef.current.addEventListener('touchmove', function (e) {
      if (prevent) {
        prevent = false
        e.preventDefault()
      }
    })
  }, [containerRef.current])

  useEffect(() => {
    if (images.length === selectedMotifs.length) {
      setImageLoadingIsFinished(true)
      if (imageLoadingIsFinished) {
        setImages(
          sortImages(
            images,
            selectedMotifs.map(motif => motif.originalSVGUrl || motif.vectorizedUrl!),
          ),
        )
      }
    }
    if (images.length < selectedMotifs.length) {
      loadImages(images.length)
    }
    if (images.length > selectedMotifs.length) {
      setImages(
        images.filter(img =>
          selectedMotifs
            .map(motif => motif.originalSVGUrl || motif.vectorizedUrl)
            .includes(img.src),
        ),
      )
    }
  }, [selectedMotifs, images.length, imageLoadingIsFinished])

  useEffect(() => {
    if (!exportInvoked || !canvasToExportRef.current || !exportModal) return
    // Clamp export canvas size for Safari
    if (isSafari) {
      const maxSize = SAFARI_MAX_CANVAS_SIZE
      if (canvasToExportRef.current.width > maxSize) canvasToExportRef.current.width = maxSize
      if (canvasToExportRef.current.height > maxSize) canvasToExportRef.current.height = maxSize
    }
    keepExportCanvas.current = canvasToExportRef.current
    setCanvasDataUrl(canvasToExportRef.current.toDataURL())
  }, [exportInvoked, canvasToExportRef])

  useEffect(() => {
    if (!startExport || !keepExportCanvas.current) return

    if (exportResponse === ExportLayoutModalResponse.TILES) {
      create3By3tiles()
        .then(resolvedCanvas => {
          keepExportCanvas.current = resolvedCanvas as HTMLCanvasElement
          exportCanvas()
        })
        .catch(error => {
          console.error('Error creating tiled base64:', error)
        })
    } else {
      exportCanvas()
    }
    setStartExport(false)
  }, [startExport, exportResponse])

  const exportCanvas = async () => {
    if (!keepExportCanvas.current) return
    // Clamp export canvas size for Safari
    if (isSafari) {
      const maxSize = SAFARI_MAX_CANVAS_SIZE
      if (keepExportCanvas.current.width > maxSize) keepExportCanvas.current.width = maxSize
      if (keepExportCanvas.current.height > maxSize) keepExportCanvas.current.height = maxSize
    }

    exportGeneratorCoordinatesToJSON({
      props: {
        type: MotifGeneratorType.SQUARE,
        selectedMotifs,
        rotationAngle,
      },
      planes: [jsonCoordinates.current],
      canvasSize: {
        width: canvasRefs.current[0].width,
        height: canvasRefs.current[0].height,
      },
    })
    if (isIOS || isAndroid) {
      handleExportCanvas(keepExportCanvas.current!)
    } else {
      const exportCanvasToImage = async () => {
        await handleExport(keepExportCanvas.current!)
      }
      exportCanvasToImage().catch(console.error)
    }
    setStartExport(false)
  }

  useEffect(() => {
    if (exportInvoked && canvasToExportRef.current) {
      draw(canvasToExportRef.current, EXPORT_RESOLUTION_MULTIPLIER)
    }

    if (canvasRefs.current.length !== TILE_NUMBER || selectedMotifs.length !== images.length) return

    canvasRefs.current.forEach(canvas => {
      draw(canvas, RESOLUTION_MULTIPLIER)
    })

    if (!firstTimeGenerated) {
      setFirstTimeGenerated(true)
    }
  }, [canvasRefs, canvasDataUrl, selectedMotifs, rotationAngle, images, exportInvoked])

  const draw = (canvas: HTMLCanvasElement, resolutionMultiplier: number) => {
    if (!canvas) return
    canvas.width = canvas.offsetWidth * resolutionMultiplier
    canvas.height = canvas.offsetHeight * resolutionMultiplier
    const ctx = canvas.getContext('2d')

    if (!ctx) return
    ctx.scale(resolutionMultiplier, resolutionMultiplier)
    ctx.imageSmoothingQuality = 'high'
    ctx.imageSmoothingEnabled = true

    ctx.beginPath()
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const CENTER_MOTIF_SIZE = canvas.offsetWidth / 2
    const AROUND_MOTIFS_PER_LINE = 8
    const AROUND_MOTIF_SIZE = canvas.offsetWidth / 8

    jsonCoordinates.current.entries = []

    const draw = (index: number, x: number, y: number, sizeX: number, sizeY: number) => {
      if (!ctx || images.length === 0) return

      ctx.drawImage(images[index], x, y, sizeX, sizeY)

      const { origin, scale, rotation } = getTransformProperties(ctx)
      jsonCoordinates.current.entries.push({
        id: selectedMotifs[index].id,
        x: origin.x,
        y: origin.y,
        size: Math.max(sizeX, sizeY) * scale,
        rotation,
      })
    }

    ctx.save()
    ctx.translate(canvas.width / resolutionMultiplier / 2, canvas.height / resolutionMultiplier / 2)
    ctx.rotate(rotationAngle)
    draw(0, -CENTER_MOTIF_SIZE / 2, -CENTER_MOTIF_SIZE / 2, CENTER_MOTIF_SIZE, CENTER_MOTIF_SIZE)

    ctx.restore()

    let verticalLineIndex: number = 0
    let horizontalLineIndex: number = 0

    const aroundMotifFactory = (index: number, type: FrameOfMotifs) => {
      if (type === FrameOfMotifs.LEFT) {
        ctx.save()
        ctx.translate(
          AROUND_MOTIF_SIZE / 2,
          horizontalLineIndex * AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE / 2,
        )
        ctx.rotate(rotationAngle)
        draw(
          index,
          -AROUND_MOTIF_SIZE / 2,
          -AROUND_MOTIF_SIZE / 2,
          AROUND_MOTIF_SIZE,
          AROUND_MOTIF_SIZE,
        )
        ctx.restore()
      }
      if (type === FrameOfMotifs.RIGHT) {
        ctx.save()
        ctx.translate(
          verticalLineIndex * AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE / 2,
          horizontalLineIndex * AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE / 2,
        )
        ctx.rotate(-rotationAngle)
        draw(
          index,
          -AROUND_MOTIF_SIZE / 2,
          -AROUND_MOTIF_SIZE / 2,
          AROUND_MOTIF_SIZE,
          AROUND_MOTIF_SIZE,
        )
        ctx.restore()
      }
      if (type === FrameOfMotifs.TOP) {
        ctx.save()
        ctx.translate(
          verticalLineIndex * AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE / 2,
          AROUND_MOTIF_SIZE / 2,
        )
        ctx.rotate(verticalLineIndex < 4 ? rotationAngle : -rotationAngle)
        draw(
          index,
          -AROUND_MOTIF_SIZE / 2,
          -AROUND_MOTIF_SIZE / 2,
          AROUND_MOTIF_SIZE,
          AROUND_MOTIF_SIZE,
        )
        ctx.restore()
      }
      if (type === FrameOfMotifs.BOTTOM) {
        ctx.save()
        ctx.translate(
          verticalLineIndex * AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE / 2,
          horizontalLineIndex * AROUND_MOTIF_SIZE + AROUND_MOTIF_SIZE / 2,
        )
        ctx.rotate(verticalLineIndex < 4 ? rotationAngle : -rotationAngle)
        draw(
          index,
          -AROUND_MOTIF_SIZE / 2,
          -AROUND_MOTIF_SIZE / 2,
          AROUND_MOTIF_SIZE,
          AROUND_MOTIF_SIZE,
        )
        ctx.restore()
      }
    }

    for (verticalLineIndex = 0; verticalLineIndex < AROUND_MOTIFS_PER_LINE; verticalLineIndex++) {
      for (
        horizontalLineIndex = 0;
        horizontalLineIndex < AROUND_MOTIFS_PER_LINE;
        horizontalLineIndex++
      ) {
        const leftLineCondition =
          verticalLineIndex === 0 && horizontalLineIndex !== AROUND_MOTIFS_PER_LINE - 2
        const rightLineCondition =
          verticalLineIndex === AROUND_MOTIFS_PER_LINE - 1 &&
          horizontalLineIndex !== AROUND_MOTIFS_PER_LINE - 2
        const topLineCondition = horizontalLineIndex === 0
        const bottomLineCondition = horizontalLineIndex === AROUND_MOTIFS_PER_LINE - 1
        if (selectedMotifs.length === 1) {
          if (leftLineCondition) aroundMotifFactory(0, FrameOfMotifs.LEFT)
          if (rightLineCondition) aroundMotifFactory(0, FrameOfMotifs.RIGHT)
          if (topLineCondition) aroundMotifFactory(0, FrameOfMotifs.TOP)
          if (bottomLineCondition) aroundMotifFactory(0, FrameOfMotifs.BOTTOM)
        } else if (selectedMotifs.length === 2) {
          if (leftLineCondition) aroundMotifFactory(1, FrameOfMotifs.LEFT)
          if (rightLineCondition) aroundMotifFactory(1, FrameOfMotifs.RIGHT)
          if (topLineCondition) aroundMotifFactory(1, FrameOfMotifs.TOP)
          if (bottomLineCondition) aroundMotifFactory(1, FrameOfMotifs.BOTTOM)
        } else if (selectedMotifs.length === 3) {
          const verticalLineCondition = horizontalLineIndex === 1 || horizontalLineIndex === 4
          const horizontalLineCondition =
            verticalLineIndex === 0 ||
            verticalLineIndex === 2 ||
            verticalLineIndex === 5 ||
            verticalLineIndex === 7
          if (leftLineCondition)
            verticalLineCondition
              ? aroundMotifFactory(1, FrameOfMotifs.LEFT)
              : aroundMotifFactory(2, FrameOfMotifs.LEFT)
          if (rightLineCondition)
            verticalLineCondition
              ? aroundMotifFactory(1, FrameOfMotifs.RIGHT)
              : aroundMotifFactory(2, FrameOfMotifs.RIGHT)
          if (topLineCondition)
            horizontalLineCondition
              ? aroundMotifFactory(1, FrameOfMotifs.TOP)
              : aroundMotifFactory(2, FrameOfMotifs.TOP)
          if (bottomLineCondition)
            horizontalLineCondition
              ? aroundMotifFactory(1, FrameOfMotifs.BOTTOM)
              : aroundMotifFactory(2, FrameOfMotifs.BOTTOM)
        } else if (selectedMotifs.length === 4) {
          if (leftLineCondition)
            if (horizontalLineIndex === 0 || horizontalLineIndex === 5) {
              aroundMotifFactory(2, FrameOfMotifs.LEFT)
            } else if (horizontalLineIndex === 1 || horizontalLineIndex === 4) {
              aroundMotifFactory(3, FrameOfMotifs.LEFT)
            } else if (horizontalLineIndex === 2 || horizontalLineIndex === 3) {
              aroundMotifFactory(1, FrameOfMotifs.LEFT)
            }
          if (rightLineCondition)
            if (horizontalLineIndex === 0 || horizontalLineIndex === 5) {
              aroundMotifFactory(2, FrameOfMotifs.RIGHT)
            } else if (horizontalLineIndex === 1 || horizontalLineIndex === 4) {
              aroundMotifFactory(3, FrameOfMotifs.RIGHT)
            } else if (horizontalLineIndex === 2 || horizontalLineIndex === 3) {
              aroundMotifFactory(1, FrameOfMotifs.RIGHT)
            }
          if (topLineCondition)
            if (verticalLineIndex === 1 || verticalLineIndex === 6) {
              aroundMotifFactory(2, FrameOfMotifs.TOP)
            } else if (verticalLineIndex === 2 || verticalLineIndex === 5) {
              aroundMotifFactory(3, FrameOfMotifs.TOP)
            } else {
              aroundMotifFactory(1, FrameOfMotifs.TOP)
            }
          if (bottomLineCondition)
            if (verticalLineIndex === 1 || verticalLineIndex === 6) {
              aroundMotifFactory(2, FrameOfMotifs.BOTTOM)
            } else if (verticalLineIndex === 2 || verticalLineIndex === 5) {
              aroundMotifFactory(3, FrameOfMotifs.BOTTOM)
            } else {
              aroundMotifFactory(1, FrameOfMotifs.BOTTOM)
            }
        }
      }
    }
  }

  useEffect(() => {
    if (!firstTimeGenerated) return
    setCanvasDataUrl(canvasRefs.current[0].toDataURL())
  }, [firstTimeGenerated])

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    onStartEvent(event.pageX, event.pageY)
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    onStartEvent(event.touches[0].pageX, event.touches[0].pageY)
  }

  const onStartEvent = (x: number, y: number) => {
    lastPos.current.x = x
    lastPos.current.y = y
    dragging.current = true
  }

  const handleMouseUp = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    setCanvasDataUrl(canvasRefs.current[0].toDataURL())
    dragging.current = false
  }

  const handleMouseMove = (event: React.MouseEvent | React.TouchEvent): void => {
    if (dragging.current) {
      const pageXY = getPagePositionXY(event)
      const moveX = lastPos.current.x - pageXY.x
      lastPos.current = pageXY
      setRotationAngle(rotationAngle + moveX / 100)
    }
  }

  const { callback } = useThrottleFn(handleMouseMove, 40)

  function create3By3tiles() {
    const tilesX = TILES_MULTIPLIER
    const tilesY = TILES_MULTIPLIER
    // PC: 9x 3000x3000 -> 9000x9000 -> 3000x3000
    // Safari: 9x 1000x1000 -> 3000x3000
    return new Promise((resolve, reject) => {
      const tileCanvases = []
      const tileSize = isSafari ? 1000 : 3000
      const exportSize = isSafari ? 3000 : 9000
      for (let i = 0; i < tilesX * tilesY; i++) {
        const tile = document.createElement('canvas')
        tile.width = tileSize
        tile.height = tileSize
        const ctx = tile.getContext('2d')
        if (ctx && keepExportCanvas.current) {
          ctx.drawImage(
            keepExportCanvas.current,
            0,
            0,
            keepExportCanvas.current.width,
            keepExportCanvas.current.height,
            0,
            0,
            tileSize,
            tileSize,
          )
        }
        tileCanvases.push(tile)
      }
      const exportCanvas = document.createElement('canvas')
      exportCanvas.width = exportSize
      exportCanvas.height = exportSize
      const exportCtx = exportCanvas.getContext('2d')
      for (let y = 0; y < tilesY; y++) {
        for (let x = 0; x < tilesX; x++) {
          const idx = y * tilesX + x
          if (exportCtx) {
            exportCtx.drawImage(
              tileCanvases[idx],
              0,
              0,
              tileSize,
              tileSize,
              x * tileSize,
              y * tileSize,
              tileSize,
              tileSize,
            )
          }
        }
      }
      if (!isSafari) {
        const finalCanvas = document.createElement('canvas')
        finalCanvas.width = 3000
        finalCanvas.height = 3000
        const finalCtx = finalCanvas.getContext('2d')
        if (finalCtx) {
          finalCtx.drawImage(
            exportCanvas,
            0,
            0,
            exportCanvas.width,
            exportCanvas.height,
            0,
            0,
            3000,
            3000,
          )
        }
        exportCanvas.remove()
        tileCanvases.forEach(c => c.remove())
        resolve(finalCanvas)
      } else {
        tileCanvases.forEach(c => c.remove())
        resolve(exportCanvas)
      }
    })
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <div
        className="flex h-[120vw] w-[120vw] flex-wrap [--tiles-per-row:3] after:absolute after:left-0 after:top-0 after:z-10 after:size-full after:bg-white after:content-['']"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={callback}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={callback}
        onTouchEnd={handleMouseUp}
      >
        {[...Array(TILE_NUMBER)].map((_, i) => {
          return (
            <div
              className="relative z-20 h-[calc(100%/var(--tiles-per-row))] w-[calc(100%/var(--tiles-per-row))] overflow-hidden bg-white bg-contain bg-center bg-no-repeat"
              key={i}
            >
              <canvas
                className="size-full"
                ref={el => {
                  canvasRefs.current[i] = el!
                }}
              ></canvas>
            </div>
          )
        })}
        {exportModal ? (
          <div
            className="absolute left-0 top-0 z-[1] overflow-hidden bg-white bg-contain bg-center bg-no-repeat"
            style={{ width: EXPORT_SIZE, height: EXPORT_SIZE }}
          >
            <canvas
              className="canvas-to-export size-full"
              width={EXPORT_SIZE / RESOLUTION_MULTIPLIER}
              height={EXPORT_SIZE / RESOLUTION_MULTIPLIER}
              ref={canvasToExportRef}
            ></canvas>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default SquareLayoutGeneratorCanvas
